/* eslint-disable no-unused-vars, react-hooks/exhaustive-deps, eqeqeq */
import React, { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { Users, CalendarClock, DollarSign, Sparkles, QrCode, Activity, Scissors, CheckCircle2, AlertTriangle, Clock, RefreshCw, X, TrendingUp, UserCheck, Award, ChevronDown } from "lucide-react";
import toast from 'react-hot-toast';
import barberDashboardApi from '../../services/barberDashboardApi';
import { subscribeDashboardRefresh } from '../../utils/dashboardRefreshBus';
import { useAuth } from '../../context/AuthContext';
import BookingsCalendar from '../../components/admin/BookingsCalendar';
import axiosClient from '../../api/axiosClient';
import { useTranslation } from 'react-i18next';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  Legend,
  ComposedChart,
  Line
} from "recharts";

const ACCENT = "#7d6633";
const COLORS = [ACCENT, "#9ca3af", "#60a5fa", "#34d399", "#fbbf24"];

const Card = ({ children, className = "" }) => (
  <div className={"rounded-2xl shadow-lg border border-white/20 dark:border-white/10 bg-white/70 dark:bg-zinc-900/60 backdrop-blur-xl " + className}>
    {children}
  </div>
);

const CardHeader = ({ title, icon, action, subtitle }) => (
  <div className="flex items-center justify-between p-2.5 md:p-3 border-b border-white/10 dark:border-white/10">
    <div className="flex items-center gap-2">
      <div className="h-8 w-8 rounded-lg bg-[--accent]/10 flex items-center justify-center" style={{ '--accent': ACCENT }}>
        {icon}
      </div>
      <div>
        <h3 className="font-semibold text-sm text-zinc-900 dark:text-zinc-100">{title}</h3>
        {subtitle && (
          <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">{subtitle}</p>
        )}
      </div>
    </div>
    {action}
  </div>
);

const Stat = ({ label, value, delta, icon }) => (
  <motion.div whileHover={{ y: -2, scale: 1.01 }} transition={{ duration: 0.2 }} className="group h-full">
    <Card className="p-2 md:p-2.5 xl:p-3 border-l-2 xl:border-l-4 h-full min-h-[104px]" style={{ borderLeftColor: ACCENT }}>
      <div className="h-full flex flex-col items-center justify-center text-center gap-1.5">
        {/* Icon centered */}
        <div className="h-8 w-8 md:h-9 md:w-9 xl:h-10 xl:w-10 rounded-lg border border-white/10 dark:border-white/10 flex items-center justify-center group-hover:scale-105 group-hover:rotate-3 transition-all duration-300" style={{ background: `${ACCENT}25` }}>
          {React.cloneElement(icon, {
            className: "h-4 w-4 md:h-[18px] md:w-[18px] xl:h-5 xl:w-5",
            style: { color: ACCENT },
          })}
        </div>

        {/* Label + value centered */}
        <div className="w-full min-w-0">
          <p className="text-[10px] md:text-[11px] xl:text-xs uppercase tracking-wide font-semibold text-zinc-500 dark:text-zinc-400 leading-tight truncate">
            {label}
          </p>
          <div className="mt-0.5 flex items-baseline justify-center gap-1">
            <span className="text-lg md:text-xl xl:text-2xl font-bold text-zinc-900 dark:text-zinc-100 truncate max-w-full">
              {value}
            </span>
            {delta && (
              <span className={`text-[10px] md:text-[11px] font-semibold ${delta.startsWith("+") ? "text-emerald-600 dark:text-emerald-400" : "text-rose-600 dark:text-rose-400"}`}>
                {delta}
              </span>
            )}
          </div>
        </div>
      </div>
    </Card>
  </motion.div>
);

const Progress = ({ value }) => (
  <div className="w-full h-2 bg-zinc-200/70 dark:bg-zinc-800 rounded-full overflow-hidden">
    <div className="h-full" style={{ width: `${value}%`, background: ACCENT }} />
  </div>
);

const StatusBadge = ({ status, t }) => {
  const statusLabelMap = {
    Paid: t('admin.status_paid'),
    Pending: t('admin.status_pending'),
    Canceled: t('admin.status_cancelled'),
    Cancelled: t('admin.status_cancelled'),
    Confirmed: t('admin.status_confirmed'),
    Completed: t('admin.status_completed'),
    Active: t('admin.status_active')
  };
  const map = {
    Paid: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300",
    Pending: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300",
    Canceled: "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300",
    Confirmed: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
    Completed: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300",
  };
  return (
    <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${map[status] || "bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300"}`}>
      {statusLabelMap[status] || status}
    </span>
  );
};

const SectionTitle = ({ children }) => (
  <h2 className="text-sm font-semibold tracking-wide text-zinc-700 dark:text-zinc-200">{children}</h2>
);

function ComposedRevenueChart({ data }) {
  const chartData = Array.isArray(data) ? data.map(item => ({
    date: item.date || item.originalDate || 'Unknown',
    revenue: Number(item.revenue) || 0,
    bookings: Number(item.bookings) || 0
  })) : [];
  
  if (chartData.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500">
        <div className="text-center">
          <p>No data available</p>
        </div>
      </div>
    );
  }
  
  return (
    <ComposedChart data={chartData} margin={{ top: 20, right: 40, left: 30, bottom: 30 }} width={400} height={200}>
      <defs>
        <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={ACCENT} stopOpacity={0.8} />
          <stop offset="100%" stopColor={ACCENT} stopOpacity={0.3} />
        </linearGradient>
      </defs>
      <CartesianGrid strokeDasharray="2 4" stroke="rgba(120,120,120,0.1)" vertical={false} />
      <XAxis dataKey="date" stroke="rgba(156,163,175,0.6)" tick={{ fontSize: 11, fill: "rgba(156,163,175,0.8)", fontWeight: 500 }} axisLine={false} tickLine={false} tickMargin={12} height={30} />
      <YAxis yAxisId="left" stroke="rgba(156,163,175,0.6)" tick={{ fontSize: 11, fill: "rgba(156,163,175,0.8)", fontWeight: 500 }} axisLine={false} tickLine={false} tickMargin={12} width={50} tickFormatter={(value) => `$${value}`} tickCount={5} />
      <YAxis yAxisId="right" orientation="right" stroke="rgba(156,163,175,0.6)" tick={{ fontSize: 11, fill: "rgba(156,163,175,0.8)", fontWeight: 500 }} axisLine={false} tickLine={false} tickMargin={12} width={50} tickCount={5} />
      <Tooltip contentStyle={{ background: "rgba(17,24,39,0.95)", border: "1px solid rgba(75,85,99,0.3)", color: "white", borderRadius: "12px", boxShadow: "0 10px 25px rgba(0,0,0,0.4)", backdropFilter: "blur(10px)" }} formatter={(value, name) => [name === 'Revenue ($)' ? `$${value}` : `${value} bookings`, name]} labelStyle={{ color: "#f3f4f6", fontWeight: 600, fontSize: "13px" }} itemStyle={{ color: "#f3f4f6", fontSize: "12px" }} />
      <Legend wrapperStyle={{ paddingTop: "25px", fontSize: "12px", fontWeight: 500 }} iconType="rect" verticalAlign="bottom" height={36} />
      <Bar yAxisId="left" dataKey="revenue" name="Revenue ($)" fill="url(#revenueGradient)" radius={[6, 6, 0, 0]} maxBarSize={50} stroke={ACCENT} strokeWidth={1} />
      <Line yAxisId="right" type="monotone" dataKey="bookings" name="Bookings" stroke="#60a5fa" strokeWidth={3} dot={{ r: 6, fill: "#60a5fa", strokeWidth: 3, stroke: "#ffffff", filter: "drop-shadow(0 2px 4px rgba(96,165,250,0.3))" }} activeDot={{ r: 8, fill: "#60a5fa", strokeWidth: 3, stroke: "#ffffff", filter: "drop-shadow(0 4px 8px rgba(96,165,250,0.4))" }} strokeDasharray="0" />
    </ComposedChart>
  );
}

export default function BarberDashboard() {
  const { user } = useAuth();
  const { t } = useTranslation('admin');
  const barberId = user?.id;
  
  console.log('🔍 BarberDashboard - User object:', user);
  console.log('🔍 BarberDashboard - Barber ID:', barberId, 'type:', typeof barberId);

  const [dashboardData, setDashboardData] = useState({
    stats: { totals: { bookings: 0, revenue_cents: 0, clients: 0 }, last24h: { bookings: 0 }, completion_rate: 0 },
    bookingsOverTime: [],
    revenueVsBookings: [],
    serviceMix: [],
    todaySchedule: [],
    recentBookings: [],
    clients: [],
    clientsTotal: 0,
    availability: { weekly: [], exceptions: [] }
  });

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedRange, setSelectedRange] = useState('7D');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [showCustomPicker, setShowCustomPicker] = useState(false);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  const loadDashboardData = useCallback(async (showRefresh = false) => {
    if (!barberId) return;

    try {
      if (showRefresh) setRefreshing(true);
      else setLoading(true);

      const urlShopId =
        typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('shopId') : null;
      const resolvedShopId = user?.shop_id || urlShopId || null;

      const fetchClientsTotal = async () => {
        if (!resolvedShopId) return 0;
        const params = new URLSearchParams({
          page: '1',
          pageSize: '1',
          shop_id: String(resolvedShopId),
          q: '',
          status: 'active',
        });
        const response = await axiosClient.get(`/admin/clients?${params}`);
        if (response?.data?.ok) {
          return Number(response.data?.pagination?.total) || 0;
        }
        // Fallback for unexpected shapes
        const data = Array.isArray(response?.data) ? response.data : (response?.data?.data || []);
        return Array.isArray(data) ? data.length : 0;
      };

      const [stats, bookingsOverTime, serviceMix, todaySchedule, recentBookings, clients, clientsTotal, availability] = await Promise.all([
        barberDashboardApi.getBarberStats(barberId, selectedRange.toLowerCase(), customFrom || null, customTo || null),
        barberDashboardApi.getBarberBookingsOverTime(barberId, selectedRange === '7D' ? 7 : selectedRange === '30D' ? 30 : 1, customFrom || null, customTo || null),
        barberDashboardApi.getBarberServiceMix(barberId),
        barberDashboardApi.getBarberTodaySchedule(barberId),
        barberDashboardApi.getBarberRecentBookings(barberId),
        barberDashboardApi.getBarberClients(barberId),
        fetchClientsTotal(),
        barberDashboardApi.getBarberAvailability(barberId)
      ]);

      setDashboardData({
        stats: stats || dashboardData.stats,
        bookingsOverTime: Array.isArray(bookingsOverTime) ? bookingsOverTime : [],
        revenueVsBookings: Array.isArray(bookingsOverTime) ? bookingsOverTime : [],
        serviceMix: Array.isArray(serviceMix) ? serviceMix : [],
        todaySchedule: Array.isArray(todaySchedule) ? todaySchedule : [],
        recentBookings: Array.isArray(recentBookings) ? recentBookings : [],
        clients: Array.isArray(clients) ? clients : [],
        clientsTotal: Number.isFinite(Number(clientsTotal)) ? Number(clientsTotal) : 0,
        availability: availability || { weekly: [], exceptions: [] }
      });

    } catch (error) {
      console.error('Failed to load barber dashboard data:', error);
      toast.error(t('admin.error_loading_barber_dashboard'));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [barberId, selectedRange, customFrom, customTo, t, user?.shop_id]);

  useEffect(() => {
    loadDashboardData();

    // Listen for booking events
    const handleBookingCreated = (event) => {
      console.log('🔄 Booking created, refreshing barber dashboard...', event.detail);
      // Only refresh if the booking is for this barber
      if (event.detail.barber_id == barberId) {
        loadDashboardData(true);
      }
    };
    
    const handleBookingRescheduled = (event) => {
      console.log('🔄 Booking rescheduled, refreshing barber dashboard...', event.detail);
      // Only refresh if the booking is for this barber
      if (event.detail.barber_id == barberId) {
        loadDashboardData(true);
      }
    };
    
    const handleBookingReassigned = (event) => {
      console.log('🔄 Booking reassigned, refreshing barber dashboard...', event.detail);
      // Refresh if booking was reassigned to or from this barber
      if (event.detail.old_barber_id == barberId || event.detail.new_barber_id == barberId) {
        loadDashboardData(true);
      }
    };
    
    const handleBookingCancelled = (event) => {
      console.log('🔄 Booking cancelled, refreshing barber dashboard...', event.detail);
      // Only refresh if the booking is for this barber
      if (event.detail.barber_id == barberId) {
        loadDashboardData(true);
      }
    };

    const unsubscribeDashboardRefresh = subscribeDashboardRefresh((detail) => {
      if (!detail) return;
      if (
        String(detail.barber_id) === String(barberId) ||
        String(detail.old_barber_id) === String(barberId) ||
        String(detail.new_barber_id) === String(barberId)
      ) {
        loadDashboardData(true);
      }
    });
    
    window.addEventListener('bookingCreated', handleBookingCreated);
    window.addEventListener('bookingRescheduled', handleBookingRescheduled);
    window.addEventListener('bookingReassigned', handleBookingReassigned);
    window.addEventListener('bookingCancelled', handleBookingCancelled);
    
    return () => {
      unsubscribeDashboardRefresh();
      window.removeEventListener('bookingCreated', handleBookingCreated);
      window.removeEventListener('bookingRescheduled', handleBookingRescheduled);
      window.removeEventListener('bookingReassigned', handleBookingReassigned);
      window.removeEventListener('bookingCancelled', handleBookingCancelled);
    };
  }, [loadDashboardData, barberId]);

  const handleRangeChange = (range) => {
    setSelectedRange(range);
    setShowCustomPicker(false);
    setCustomFrom('');
    setCustomTo('');
  };

  const handleCustomFromChange = (date) => {
    setCustomFrom(date);
    if (date && customTo) setSelectedRange('Custom');
  };

  const handleCustomToChange = (date) => {
    setCustomTo(date);
    if (customFrom && date) setSelectedRange('Custom');
  };

  const handleRefresh = () => loadDashboardData(true);

  // Range controls reused for mobile and desktop placements
  const RangeControls = ({ className = "" }) => (
    <div className={`flex items-center gap-2 ${className}`}>
      <button onClick={handleRefresh} disabled={refreshing} className={`px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 ${refreshing ? 'cursor-not-allowed' : ''}`}>
        <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
      </button>
      <button onClick={() => handleRangeChange('Today')} className={`px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 ${selectedRange === 'Today' ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' : 'text-gray-700 dark:text-gray-200'}`}>{t('admin.today')}</button>
      <button onClick={() => handleRangeChange('7D')} className={`px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 ${selectedRange === '7D' ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' : 'text-gray-700 dark:text-gray-200'}`}>{t('admin.range_7d')}</button>
      <button onClick={() => handleRangeChange('30D')} className={`px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 ${selectedRange === '30D' ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' : 'text-gray-700 dark:text-gray-200'}`}>{t('admin.range_30d')}</button>
      <button onClick={() => setShowCustomPicker(!showCustomPicker)} className={`px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 ${selectedRange === 'Custom' ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' : 'text-gray-700 dark:text-gray-200'}`}>{t('admin.range_custom')}</button>
    </div>
  );

  if (loading) {
    return (
      <div className="w-full">
        <div className="flex items-center justify-center min-h-96">
          <div className="flex items-center gap-3 text-zinc-600 dark:text-zinc-400">
            <RefreshCw className="h-6 w-6 animate-spin" />
            <span className="text-lg">{t('admin.loading_barber_dashboard')}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* Header */}
      <div className="mb-4 md:mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-4">
          <div className="flex-1 min-w-0">
            <h1 className="text-xl md:text-2xl font-bold flex items-center text-zinc-900 dark:text-zinc-100">
              <svg
                className="h-5 w-5 md:h-6 md:w-6 mr-2 flex-shrink-0"
                style={{ color: ACCENT }}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                />
              </svg>
              {t('admin.barber_dashboard_title')}
            </h1>
            <p className="text-xs md:text-sm text-zinc-600 dark:text-zinc-400 mt-0.5 md:mt-1">
              {t('admin.barber_dashboard_subtitle')}
            </p>
          </div>

          {/* Desktop placement: show range controls on the right */}
          <div className="hidden md:flex flex-shrink-0">
            <RangeControls />
          </div>
        </div>

        {/* Mobile placement: show range controls below, left-aligned */}
        <div className="mt-3 md:hidden">
          <RangeControls className="w-full justify-start hc-range-scroll hc-range" />
        </div>
      </div>

      {/* Calendar Section */}
      <div className="mb-6">
        <SectionTitle>{t('admin.section_calendar_view')}</SectionTitle>
        <Card className="mt-4">
          <CardHeader
            title={t('admin.barber_my_calendar')}
            subtitle={t('admin.barber_my_calendar_subtitle')}
            icon={<CalendarClock className="h-5 w-5" />}
            action={
              <button
                onClick={() => setIsCalendarOpen((v) => !v)}
                aria-label={t('admin.toggle_calendar')}
                className="px-3 py-1.5 rounded-lg border border-white/10 dark:border-white/10 hover:bg-black/5 dark:hover:bg-white/5 transition-all text-xs font-medium"
              >
                {isCalendarOpen ? t('admin.calendar_hide') : t('admin.calendar_show')}
              </button>
            }
          />
          {isCalendarOpen && (
            <div className="p-3 md:p-4">
              <BookingsCalendar mode="barber" />
            </div>
          )}
        </Card>
      </div>

      {showCustomPicker && (
        <Card className="mb-6 p-5">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-end">
            <div className="sm:col-span-1">
              <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">{t('admin.from_date')}</label>
              <input type="date" value={customFrom} onChange={(e) => handleCustomFromChange(e.target.value)} className="w-full px-4 py-3 rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[--accent]/20 focus:border-[--accent]/30 transition-all" style={{ '--accent': ACCENT }} />
            </div>
            <div className="sm:col-span-1">
              <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">{t('admin.to_date')}</label>
              <input type="date" value={customTo} onChange={(e) => handleCustomToChange(e.target.value)} className="w-full px-4 py-3 rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[--accent]/20 focus:border-[--accent]/30 transition-all" style={{ '--accent': ACCENT }} />
            </div>
            <div className="sm:col-span-1 flex justify-end">
              <button onClick={() => setShowCustomPicker(false)} className="w-full sm:w-auto px-4 py-3 rounded-xl border border-white/20 dark:border-white/10 bg-white/70 dark:bg-zinc-900/60 backdrop-blur-xl text-zinc-700 dark:text-zinc-300 hover:bg-white/80 dark:hover:bg-zinc-900/80 transition-all">
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        </Card>
      )}

      {/* Content */}
      <div className="space-y-6">
        {/* Key Performance Indicators */}
        <div>
          <SectionTitle>{t('admin.section_key_metrics')}</SectionTitle>
          <div className="mt-3 flex flex-nowrap gap-1.5 sm:gap-2 md:gap-2.5 lg:gap-3 xl:gap-4">
            <div className="flex-1 min-w-0">
              <Stat 
                label={t('admin.barber_stat_my_bookings')} 
                value={dashboardData.stats?.totals?.bookings?.toLocaleString() || "0"} 
                delta="" 
                icon={<CalendarClock />} 
              />
            </div>
            <div className="flex-1 min-w-0">
              <Stat 
                label={t('admin.barber_stat_my_revenue')} 
                value={`$${((dashboardData.stats?.totals?.revenue_cents || 0) / 100).toLocaleString()}`} 
                delta="" 
                icon={<DollarSign />} 
              />
            </div>
            <div className="flex-1 min-w-0">
              <Stat 
                label={t('admin.barber_stat_my_clients')} 
                value={(dashboardData.clientsTotal || 0).toString()} 
                delta="" 
                icon={<UserCheck />} 
              />
            </div>
          </div>
        </div>

        {/* Performance Overview */}
        <div>
          <SectionTitle>{t('admin.section_performance_overview')}</SectionTitle>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-5 mt-4">
          <Card>
            <CardHeader title={t('admin.barber_bookings_over_time')} subtitle={t('admin.barber_daily_trend')} icon={<Activity className="h-4 w-4" />} />
            <div className="h-60 p-3 md:p-4">
              {(dashboardData.bookingsOverTime || []).length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={dashboardData.bookingsOverTime} margin={{ left: 0, right: 0, top: 10, bottom: 0 }}>
                    <defs>
                      <linearGradient id="fillBookings" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="10%" stopColor={ACCENT} stopOpacity={0.5} />
                        <stop offset="95%" stopColor={ACCENT} stopOpacity={0.05} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(120,120,120,0.15)" />
                    <XAxis dataKey="date" stroke="currentColor" className="text-zinc-400 dark:text-zinc-500" />
                    <YAxis stroke="currentColor" className="text-zinc-400 dark:text-zinc-500" />
                    <Tooltip contentStyle={{ background: "rgba(24,24,27,0.9)", border: "1px solid rgba(255,255,255,0.1)", color: "white" }} labelStyle={{ color: "#e5e7eb" }} />
                    <Area type="monotone" dataKey="bookings" stroke={ACCENT} fill="url(#fillBookings)" strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full text-zinc-500 dark:text-zinc-400">
                  <div className="text-center">
                    <Activity className="h-8 w-8 mx-auto mb-2 opacity-30" />
                    <p className="text-xs">{t('admin.no_data')}</p>
                  </div>
                </div>
              )}
            </div>
          </Card>

          <Card>
            <CardHeader title={t('admin.barber_revenue_vs_bookings')} subtitle={t('admin.barber_revenue_bookings_trend')} icon={<TrendingUp className="h-4 w-4" />} />
            <div className="h-60 p-3 md:p-4">
              {(dashboardData.revenueVsBookings || []).length > 0 ? (
                <div style={{ width: '100%', height: '100%' }}>
                  <ComposedRevenueChart data={dashboardData.revenueVsBookings} />
                </div>
              ) : (
                <div className="flex items-center justify-center h-full text-zinc-500 dark:text-zinc-400">
                  <div className="text-center">
                    <TrendingUp className="h-8 w-8 mx-auto mb-2 opacity-30" />
                    <p className="text-xs">{t('admin.no_data')}</p>
                  </div>
                </div>
              )}
            </div>
          </Card>

          <Card>
            <CardHeader title={t('admin.barber_popular_services')} subtitle={t('admin.popular_services_subtitle')} icon={<QrCode className="h-4 w-4" />} />
            <div className="h-60 p-2">
              {(dashboardData.serviceMix || []).length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={dashboardData.serviceMix} dataKey="value" nameKey="name" innerRadius={35} outerRadius={65} paddingAngle={2}>
                      {dashboardData.serviceMix.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Legend 
                      wrapperStyle={{ fontSize: '10px' }}
                      iconSize={6}
                    />
                    <Tooltip 
                      contentStyle={{ 
                        background: "rgba(255,255,255,0.95)", 
                        border: "1px solid rgba(0,0,0,0.1)", 
                        color: "rgba(0,0,0,0.8)",
                        borderRadius: "8px",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                        fontSize: "11px"
                      }} 
                      labelStyle={{ 
                        color: "rgba(0,0,0,0.8)", 
                        fontWeight: 600,
                        fontSize: "11px"
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full text-zinc-500 dark:text-zinc-400">
                  <div className="text-center">
                    <QrCode className="h-8 w-8 mx-auto mb-2 opacity-30" />
                    <p className="text-xs">{t('admin.no_data')}</p>
                  </div>
                </div>
              )}
            </div>
          </Card>
          </div>
        </div>

        {/* Recent Activity */}
        <div>
          <SectionTitle>{t('admin.section_recent_activity')}</SectionTitle>
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 md:gap-5 mt-4">
          <Card className="lg:col-span-3">
            <CardHeader title={t('admin.barber_recent_clients')} subtitle={t('admin.barber_recent_clients_subtitle')} icon={<Users className="h-4 w-4" />} />
            <div className="p-3 overflow-x-auto">
              {(dashboardData.clients || []).length > 0 ? (
                <table className="min-w-full text-xs">
                  <thead>
                    <tr className="text-left text-zinc-500 dark:text-zinc-400 border-b border-zinc-200 dark:border-zinc-700">
                      <th className="py-2 px-2 font-semibold">{t('admin.col_client')}</th>
                      <th className="py-2 px-2 font-semibold">{t('admin.barber_last_visit')}</th>
                      <th className="py-2 px-2 font-semibold">{t('admin.barber_total_visits')}</th>
                      <th className="py-2 px-2 font-semibold">{t('admin.col_status')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-black/5 dark:divide-white/10">
                    {(dashboardData.clients || []).slice(0, 10).map((client, index) => (
                      <tr key={client.id || index} className="text-zinc-800 dark:text-zinc-100 hover:bg-zinc-50 dark:hover:bg-zinc-800/50 transition-colors">
                        <td className="py-2 px-2">
                          <div className="flex items-center gap-2">
                            <div className="h-8 w-8 rounded-full bg-zinc-200 dark:bg-zinc-800 flex items-center justify-center font-semibold text-xs" style={{ color: ACCENT }}>
                              {(client.name || t('admin.client_initial_fallback')).split(" ").map((n) => n[0]).join("")}
                            </div>
                            <span className="font-medium">{client.name || t('admin.unknown_client')}</span>
                          </div>
                        </td>
                        <td className="py-2 px-2 text-zinc-600 dark:text-zinc-300">{client.last_visit || t('admin.not_available')}</td>
                        <td className="py-2 px-2">{client.total_visits || 0}</td>
                        <td className="py-2 px-2"><StatusBadge status={client.status || "Active"} t={t} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="text-center py-12 text-zinc-500 dark:text-zinc-400">
                  <Users className="h-8 w-8 mx-auto mb-2 opacity-30" />
                  <p className="text-xs">{t('admin.no_clients_yet')}</p>
                </div>
              )}
            </div>
          </Card>

          <Card className="lg:col-span-1">
            <CardHeader title={t('admin.today_schedule')} subtitle={t('admin.barber_my_appointments')} icon={<Clock className="h-4 w-4" />} />
            <div className="p-2 max-h-96 overflow-y-auto">
              {(dashboardData.todaySchedule || []).length > 0 ? (
                <div className="space-y-1.5">
                  {(dashboardData.todaySchedule || []).slice(0, 6).map((s, i) => (
                    <div key={i} className="flex items-center gap-2 p-1.5 rounded-lg border border-black/5 dark:border-white/10 hover:bg-zinc-50 dark:hover:bg-zinc-800/50 transition-colors">
                      <div className="h-8 w-10 rounded-lg flex items-center justify-center text-xs font-bold flex-shrink-0" style={{ background: `${ACCENT}20`, color: ACCENT }}>
                        {typeof s.time === 'string' ? s.time : (s.start_time ? new Date(s.start_time).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : t('admin.not_available'))}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold text-zinc-900 dark:text-zinc-100 truncate">{s.client || t('admin.unknown_client')}</p>
                        <p className="text-xs text-zinc-500 dark:text-zinc-400 truncate">{typeof s.service === 'object' ? (s.service?.name || t('admin.unknown_service')) : (s.service || t('admin.unknown_service'))}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-zinc-500 dark:text-zinc-400">
                  <Clock className="h-8 w-8 mx-auto mb-2 opacity-30" />
                  <p className="text-xs">{t('admin.no_appointments_today')}</p>
                </div>
              )}
            </div>
          </Card>
          </div>
        </div>

        {/* Recent Bookings */}
        <div>
          <SectionTitle>{t('admin.recent_bookings')}</SectionTitle>
          <Card className="mt-4">
            <CardHeader title={t('admin.barber_recent_bookings')} subtitle={t('admin.recent_bookings_subtitle')} icon={<CheckCircle2 className="h-4 w-4" />} />
            <div className="p-3 overflow-x-auto">
              {(dashboardData.recentBookings || []).length > 0 ? (
                <table className="min-w-full text-xs">
                  <thead>
                    <tr className="text-left text-zinc-500 dark:text-zinc-400 border-b border-zinc-200 dark:border-zinc-700">
                      <th className="py-2 px-2 font-semibold">{t('admin.col_client')}</th>
                      <th className="py-2 px-2 font-semibold">{t('admin.col_service')}</th>
                      <th className="py-2 px-2 font-semibold">{t('admin.col_price')}</th>
                      <th className="py-2 px-2 font-semibold">{t('admin.col_date_time')}</th>
                      <th className="py-2 px-2 font-semibold">{t('admin.col_status')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-black/5 dark:divide-white/10">
                    {(dashboardData.recentBookings || []).slice(0, 10).map((r) => (
                      <tr key={r.id} className="text-zinc-800 dark:text-zinc-100 hover:bg-zinc-50 dark:hover:bg-zinc-800/50 transition-colors">
                        <td className="py-2 px-2 font-medium">{r.client}</td>
                        <td className="py-2 px-2 text-zinc-600 dark:text-zinc-300">{r.service}</td>
                        <td className="py-2 px-2 font-bold">${r.price}</td>
                        <td className="py-2 px-2">{r.date}</td>
                        <td className="py-2 px-2"><StatusBadge status={r.status} t={t} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="text-center py-12 text-zinc-500 dark:text-zinc-400">
                  <CheckCircle2 className="h-8 w-8 mx-auto mb-2 opacity-30" />
                  <p className="text-xs">{t('admin.no_bookings')}</p>
                </div>
              )}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
